import React, { useEffect, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useAuth } from '../context/AuthContext';
import { getBranding } from '../api';

interface Props {
  children: React.ReactNode;
  /** When provided, the authenticated user must have this role (or ADMIN).
   *  Supported values: "POWER_USER". If the user lacks the role, an access-denied
   *  screen is shown instead of the children. */
  requiredRole?: 'POWER_USER';
}

function RequireAuth({ children, requiredRole }: Props) {
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { user, loading, error, login, isPowerUser } = useAuth();
  const [logoUrl, setLogoUrl] = useState<string>('');

  // Fetch branding once on mount so the login screen can show the logo.
  // Uses the public /api/branding endpoint — no auth required.
  // Fails silently: if the call errors, logoUrl stays '' and the default
  // icon is shown instead.
  useEffect(() => {
    getBranding().then(b => {
      if (b.portal_logo_url) setLogoUrl(b.portal_logo_url);
    });
  }, []);

  // MSAL is still processing the redirect — don't render anything yet
  if (inProgress !== InteractionStatus.None) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', backgroundColor: '#f0f2f5',
        fontFamily: 'Segoe UI, sans-serif' }}>
        <p style={{ color: '#666' }}>Signing in...</p>
      </div>
    );
  }

  // Authenticated with Microsoft but session call still in progress
  if (isAuthenticated && loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', backgroundColor: '#f0f2f5',
        fontFamily: 'Segoe UI, sans-serif' }}>
        <p style={{ color: '#666' }}>Loading your profile...</p>
      </div>
    );
  }

  // Not authenticated — show login screen (no auto-redirect)
  if (!isAuthenticated || !user) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', backgroundColor: '#f0f2f5',
        fontFamily: 'Segoe UI, sans-serif' }}>
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '48px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.1)', textAlign: 'center',
          maxWidth: '400px', width: '100%' }}>

          {/* Logo — shows image from portal_logo_url setting, falls back to default icon */}
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Company logo"
              style={{ maxWidth: '200px', maxHeight: '80px', objectFit: 'contain',
                display: 'block', margin: '0 auto 24px' }}
            />
          ) : (
            <div style={{ width: '56px', height: '56px', borderRadius: '50%',
              backgroundColor: '#00386C', display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 24px' }}>
              <span style={{ color: 'white', fontSize: '24px' }}>P</span>
            </div>
          )}

          <h1 style={{ fontSize: '22px', color: '#1a1a1a', margin: '0 0 8px' }}>
            AP Invoice Portal
          </h1>
          <p style={{ color: '#666', fontSize: '14px', margin: '0 0 32px' }}>
            Powerlink Accounts Payable
          </p>

          {error && (
            <div style={{ backgroundColor: '#fdecea', border: '1px solid #f5c6cb',
              borderRadius: '4px', padding: '12px', marginBottom: '20px',
              fontSize: '13px', color: '#c0392b' }}>
              {error}
            </div>
          )}

          <button
            onClick={login}
            style={{ width: '100%', padding: '12px', backgroundColor: '#00386C',
              color: 'white', border: 'none', borderRadius: '6px',
              fontSize: '15px', fontWeight: 600, cursor: 'pointer' }}>
            Sign in with Microsoft
          </button>
          <p style={{ color: '#999', fontSize: '12px', marginTop: '16px' }}>
            Use your Powerlink Microsoft account
          </p>
        </div>
      </div>
    );
  }

  // Authenticated but missing required role — show access-denied screen
  if (requiredRole === 'POWER_USER' && !isPowerUser) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', backgroundColor: '#f0f2f5',
        fontFamily: 'Segoe UI, sans-serif' }}>
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '48px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.1)', textAlign: 'center',
          maxWidth: '400px', width: '100%' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '50%',
            backgroundColor: '#fdecea', display: 'flex', alignItems: 'center',
            justifyContent: 'center', margin: '0 auto 24px' }}>
            <span style={{ fontSize: '24px' }}>🔒</span>
          </div>
          <h1 style={{ fontSize: '22px', color: '#1a1a1a', margin: '0 0 8px' }}>
            Access Restricted
          </h1>
          <p style={{ color: '#666', fontSize: '14px', margin: '0 0 24px' }}>
            This section requires Power User access. Contact your administrator
            to request the AP-PowerUsers role.
          </p>
          <p style={{ color: '#999', fontSize: '12px' }}>
            Signed in as {user.display_name} ({user.role})
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export default RequireAuth;
